import { CheckCircle } from '@mui/icons-material';
import { BlockNode, blockTree, groupBlocks, isAnswered } from './flow';
import { QuestionBlockView } from './QuestionBlockView';
import {
  BlockNumber,
  DoneCheck,
  GroupCard,
  TreeChildren,
  TreeNode,
} from './styles';
import { Answers, BasketQuestionDTO, ModelChoices } from './types';

/** Até este nível a filha recua para o lado; depois disso só desce. */
const MAX_INDENTED_DEPTH = 2;

type Props = {
  /** Todas as perguntas do fluxo, para saber qual pergunta abriu qual. */
  all: BasketQuestionDTO[];
  /** Perguntas visíveis a mostrar (as da aba ativa). */
  questions: BasketQuestionDTO[];
  answers: Answers;
  models: ModelChoices;
  errorFor: string | null;
  blockRef: (code: string) => (element: HTMLDivElement | null) => void;
  onAnswer: (question: BasketQuestionDTO, value: string) => void;
  onPickModel: (question: BasketQuestionDTO, optionValue: string) => void;
  onSelectProduct: (question: BasketQuestionDTO, productId: number) => void;
  /** Número do bloco a que a pergunta pertence (igual em todas as abas). */
  blockNumber: (question: BasketQuestionDTO) => number;
};

/**
 * Perguntas ligadas ficam num cartão só, que cresce a cada resposta. Cada
 * pergunta é um card; a que depende de outra fica recuada, com uma linha
 * cinza ligando as duas.
 */
export const QuestionBlocks = ({
  all,
  questions,
  answers,
  models,
  errorFor,
  blockRef,
  onAnswer,
  onPickModel,
  onSelectProduct,
  blockNumber,
}: Props) => (
  <>
    {groupBlocks(questions, all).map((group) => {
      const done = group.every((question) =>
        isAnswered(question, answers, models)
      );
      const number = blockNumber(group[0]);

      const renderNode = (
        node: BlockNode,
        depth: number,
        isFirstOfBlock: boolean
      ) => {
        const { question } = node;
        const indented = depth + 1 <= MAX_INDENTED_DEPTH;
        return (
          <>
            <QuestionBlockView
              grouped
              question={question}
              answers={answers}
              models={models}
              showError={errorFor === question.code}
              blockRef={blockRef(question.code)}
              onAnswer={(value) => onAnswer(question, value)}
              onPickModel={(option) => onPickModel(question, option)}
              onSelectProduct={(id) => onSelectProduct(question, id)}
              leading={
                isFirstOfBlock && (
                  <BlockNumber title={`Bloco ${number}`}>{number}</BlockNumber>
                )
              }
              trailing={
                isFirstOfBlock &&
                done && (
                  <DoneCheck role="img" aria-label="Bloco concluído">
                    <CheckCircle />
                  </DoneCheck>
                )
              }
            />
            {node.children.length > 0 && (
              <TreeChildren indent={indented}>
                {node.children.map((child) => (
                  <TreeNode key={child.question.code} nested lined={indented}>
                    {renderNode(child, depth + 1, false)}
                  </TreeNode>
                ))}
              </TreeChildren>
            )}
          </>
        );
      };

      return (
        <GroupCard key={group[0].code} done={done}>
          {blockTree(group).map((root, index) => (
            <TreeNode key={root.question.code} nested={index > 0}>
              {renderNode(root, 0, index === 0)}
            </TreeNode>
          ))}
        </GroupCard>
      );
    })}
  </>
);
